"use client";

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { Search as SearchIcon, X, Book, FileText, Command } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getAllPosts, PostData } from '@/lib/markdown';

interface SearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const Search = ({ isOpen, onClose }: SearchProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [allContent, setAllContent] = useState<(PostData & { type: string })[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([
        getAllPosts('blog').then(posts => posts.map(p => ({ ...p, type: 'blog' }))),
        getAllPosts('docs').then(posts => posts.map(p => ({ ...p, type: 'docs' })))
      ]).then(([blog, docs]) => {
        setAllContent([...blog, ...docs]);
        setLoading(false);
      });
    }
  }, [isOpen]);

  const fuse = useMemo(() => {
    return new Fuse(allContent, {
      keys: ['title', 'excerpt', 'content'],
      threshold: 0.3,
      includeMatches: true
    });
  }, [allContent]);

  const results = useMemo(() => {
    if (!query) return [];
    return fuse.search(query).map(r => r.item);
  }, [fuse, query]);

  const handleSelect = (type: string, slug: string) => {
    navigate(`/${type}/${slug}`);
    onClose();
    setQuery('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] bg-slate-950/90 border-white/10 backdrop-blur-2xl p-0 overflow-hidden shadow-2xl">
        <DialogHeader className="p-4 border-b border-white/5 flex flex-row items-center gap-3">
          <SearchIcon className="w-5 h-5 text-slate-500" />
          <Input
            autoFocus
            placeholder="Search documentation and blog..."
            className="bg-transparent border-none focus-visible:ring-0 text-lg h-12 p-0"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="flex items-center gap-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            <Command className="w-3 h-3" /> K
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="p-4">
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="w-6 h-6 border-2 border-fuchsia-500/20 border-t-fuchsia-500 rounded-full animate-spin" />
              </div>
            ) : query && results.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                No results found for "<span className="text-white">{query}</span>"
              </div>
            ) : query ? (
              <div className="space-y-2">
                {results.map((item) => (
                  <button
                    key={`${item.type}-${item.slug}`}
                    onClick={() => handleSelect(item.type, item.slug)}
                    className="w-full text-left p-4 rounded-2xl hover:bg-white/5 border border-transparent hover:border-white/10 transition-all group"
                  >
                    <div className="flex items-center gap-3 mb-1">
                      {item.type === 'docs' ? (
                        <Book className="w-4 h-4 text-fuchsia-500" />
                      ) : (
                        <FileText className="w-4 h-4 text-blue-400" />
                      )}
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                        {item.type}
                      </span>
                    </div>
                    <h3 className="text-white font-bold group-hover:text-fuchsia-400 transition-colors">
                      {item.title}
                    </h3>
                    {item.excerpt && (
                      <p className="text-slate-400 text-sm line-clamp-1 mt-1">
                        {item.excerpt}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center">
                <p className="text-slate-500 text-sm">Start typing to search...</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default Search;